import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import type { AssistantAnswer } from '../api/types';
import { useProjectApi } from '../projects/useProjectApi';
import type { PageContext } from './pageContext';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  links?: AssistantAnswer['links'];
  // gesprochen (Gespräch mit der Assistentin) statt geschrieben
  voice?: boolean;
}

// Chat der KI-Hilfe: Verlauf nur im Browser (bis zum Neuladen). Mitgeschickt werden die geöffnete Seite und die letzten zehn
// Nachrichten – auch Mitschriften aus dem Gespräch mit der Assistentin, damit der Faden erhalten bleibt.
export function useAssistantChat(page: PageContext) {
  const papi = useProjectApi();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const ask = useMutation({
    mutationFn: ({ question, history }: { question: string; history: ChatMessage[] }) =>
      papi.askAssistant({
        page: page.key,
        title: page.label,
        question,
        history: history.slice(-10).map(({ role, content }) => ({ role, content })),
      }),
    onSuccess: (res) => setMessages((m) => [...m, { role: 'assistant', content: res.answer, links: res.links }]),
  });
  const send = (text: string) => {
    const question = text.trim();
    if (!question || ask.isPending) return;
    ask.mutate({ question, history: messages });
    setMessages((m) => [...m, { role: 'user', content: question }]);
  };
  const addVoice = (role: ChatMessage['role'], content: string) => setMessages((m) => [...m, { role, content, voice: true }]);
  const reset = () => {
    setMessages([]);
    ask.reset();
  };
  return { messages, send, addVoice, reset, pending: ask.isPending, error: ask.isError ? ask.error : null };
}

export type AssistantChat = ReturnType<typeof useAssistantChat>;
